/**
 * PIMsDataTab Component
 *
 * Displays PIMs medication data for a patient with 2 sub-tabs:
 * - Overview: 4 featured scores + medication flags + detail fields
 * - Medications & Problems: Bulleted medication and problem lists
 *
 * Month is shown as a static badge (latest available), not a dropdown.
 */

import { useCallback, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { AlertCircle, AlertTriangle } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { usePatientPims } from '@/hooks/equip/useEquipPims'
import type { PIMsAuditItem } from '@/lib/equip-types'

interface PIMsDataTabProps {
  patientId: string
  year: number
  quarter: number
}

/* ---- Helpers ---- */

/** Parse a JSON array or delimited string into an array of items */
function parseListData(raw: string | null): string[] {
  if (!raw || !raw.trim()) return []
  const trimmed = raw.trim()
  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed)
      if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean)
    } catch { /* fall through */ }
  }
  let items = trimmed.split('\n').map((s) => s.trim()).filter(Boolean)
  if (items.length > 1) return items
  items = trimmed.split('|').map((s) => s.trim()).filter(Boolean)
  if (items.length > 1) return items
  return [trimmed]
}

/* ---- Score Card ---- */

function ScoreCard({ label, value }: { label: string; value: number | null }) {
  return (
    <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 text-center">
      <div className="text-2xl font-bold tabular-nums">
        {value !== null && value !== undefined ? value : (
          <span className="text-muted-foreground">N/A</span>
        )}
      </div>
      <div className="text-xs text-muted-foreground mt-1">{label}</div>
    </div>
  )
}

/* ---- Overview Content ---- */

const MEDICATION_FLAGS = [
  { key: 'antipsychotic' as const, label: 'Antipsychotics' },
  { key: 'benzodiazepine' as const, label: 'Benzos' },
  { key: 'opioids' as const, label: 'Opioids' },
  { key: 'depakoteForMood' as const, label: 'Depakote' },
]

function OverviewContent({ pimsData }: { pimsData: PIMsAuditItem }) {
  const activeFlags = MEDICATION_FLAGS.filter((f) => {
    const val = pimsData[f.key]
    return !!val
  })

  const detailFields: { label: string; value: string }[] = [
    { label: 'Total PIMs Meds', value: pimsData.totalPimsMeds !== null ? String(pimsData.totalPimsMeds) : '\u2014' },
    { label: 'Antipsychotic', value: pimsData.antipsychotic !== null ? (pimsData.antipsychotic ? 'Yes' : 'No') : '\u2014' },
    { label: 'Benzodiazepine', value: pimsData.benzodiazepine !== null ? (pimsData.benzodiazepine ? 'Yes' : 'No') : '\u2014' },
    { label: 'Opioids', value: pimsData.opioids !== null ? (pimsData.opioids ? 'Yes' : 'No') : '\u2014' },
    { label: 'Depakote for Mood', value: pimsData.depakoteForMood !== null ? (pimsData.depakoteForMood ? 'Yes' : 'No') : '\u2014' },
    { label: 'Audit Month', value: pimsData.auditMonth || '\u2014' },
  ]

  return (
    <div className="space-y-3">
      {/* Missing medication list warning */}
      {pimsData.missingMedicationList && (
        <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2.5">
          <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
          <div className="text-xs">
            <span className="font-semibold text-amber-600 dark:text-amber-400">Scores set to maximum. </span>
            <span className="text-muted-foreground">
              This patient's medication list has 1 or fewer entries. PCP PIMs score is set to 15 and
              Psych PIMs score is set to 3. The medication list may need to be updated in the EHR.
            </span>
          </div>
        </div>
      )}

      {/* 4 Score Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <ScoreCard label="PCP PIMs" value={pimsData.pcpPimsScore} />
        <ScoreCard label="Psych PIMs" value={pimsData.psychPimsScore} />
        <ScoreCard label="ACB Score" value={pimsData.acbScore} />
        <ScoreCard label="Total Routine Meds" value={pimsData.totalRoutineMeds} />
      </div>

      {/* Medication Flags */}
      {activeFlags.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {activeFlags.map((f) => (
            <Badge key={f.key} variant="lightWarning" className="text-xs">
              {f.label}
            </Badge>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">No medication flags</p>
      )}

      {/* Detail Fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 pt-1 border-t border-border/50">
        {detailFields.map((f) => (
          <div key={f.label}>
            <div className="text-xs text-muted-foreground">{f.label}</div>
            <div className="text-sm font-medium">{f.value}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

/* ---- Bulleted List ---- */

function BulletedList({ items }: { items: string[] }) {
  if (items.length === 0) return <p className="text-sm text-muted-foreground">None reported</p>
  return (
    <ul className="list-disc list-inside space-y-0.5 text-sm">
      {items.map((item, i) => (
        <li key={i} className="text-foreground">{item}</li>
      ))}
    </ul>
  )
}

/* ---- Medications & Problems Content ---- */

function MedsProblemsContent({ pimsData }: { pimsData: PIMsAuditItem }) {
  const medItems = parseListData(pimsData.medicationListShort)
  const problemItems = parseListData(pimsData.problemList)

  return (
    <div className="space-y-4">
      <div>
        <div className="text-sm font-semibold mb-1">Medications</div>
        <BulletedList items={medItems} />
      </div>
      <div>
        <div className="text-sm font-semibold mb-1">Problems</div>
        <BulletedList items={problemItems} />
      </div>
    </div>
  )
}

/* ---- Tab Trigger Styles ---- */

const tabTrigger =
  'rounded-t-md rounded-b-none border border-b-0 border-border px-3 py-1.5 text-sm bg-white shadow-none data-[state=active]:bg-primary/10 data-[state=active]:text-foreground data-[state=inactive]:text-muted-foreground'

/* ---- Main Component ---- */

export function PIMsDataTab({ patientId, year, quarter }: PIMsDataTabProps) {
  const [activeSubTab, setActiveSubTab] = useState('overview')
  const [containerHeight, setContainerHeight] = useState<number | 'auto'>('auto')
  const observerRef = useRef<ResizeObserver | null>(null)

  const measureRef = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) observerRef.current.disconnect()
    if (!node) return
    setContainerHeight(node.scrollHeight)
    observerRef.current = new ResizeObserver(() => {
      setContainerHeight(node.scrollHeight)
    })
    observerRef.current.observe(node)
  }, [])

  // React Query: fetch patient PIMs data — cache hit within 5 minutes on re-open
  const pimsQuery = usePatientPims(patientId, year, quarter)
  const pimsData: PIMsAuditItem | null = pimsQuery.data ?? null
  const isLoading = pimsQuery.isLoading
  const error = pimsQuery.error?.message ?? null

  // Loading skeleton (compact)
  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-8 w-32" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-destructive bg-destructive/10 rounded-md">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <span>{error}</span>
      </div>
    )
  }

  // Empty state
  if (!pimsData) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No PIMs data available for this patient
      </div>
    )
  }

  // Month badge
  const monthLabel = pimsData.auditMonth || null

  return (
    <div className="space-y-2">
      {/* Header with month badge */}
      {monthLabel && (
        <div className="flex items-center gap-2">
          <Badge variant="lightInfo" className="text-xs font-normal">
            {monthLabel}
          </Badge>
        </div>
      )}

      {/* Sub-tabs (boxed folder style, visually distinct from outer pill-style tabs) */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList className="bg-transparent rounded-none h-auto p-0 gap-0 -mb-px relative z-10">
          <TabsTrigger value="overview" className={tabTrigger}>
            Overview
          </TabsTrigger>
          <TabsTrigger value="meds-problems" className={tabTrigger}>
            Medications & Problems
          </TabsTrigger>
        </TabsList>

        <motion.div
          animate={{ height: containerHeight }}
          initial={false}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="border border-border rounded-b-md rounded-tr-md bg-primary/10 overflow-hidden"
        >
          <div ref={measureRef} className="p-3">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeSubTab}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
              >
                {activeSubTab === 'overview' ? (
                  <OverviewContent pimsData={pimsData} />
                ) : (
                  <MedsProblemsContent pimsData={pimsData} />
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </motion.div>
      </Tabs>
    </div>
  )
}
